<template>
  <v-dialog
    :value="show"
    :width="480"
    :persistent="busy"
    content-class="unarchive-name-conflict"
    data-testid="unarchive-name-conflict-dialog"
    @input="onDialogInput"
    @keydown.esc="onCancel"
  >
    <v-card
      class="unarchive-name-conflict__card pa-6"
      role="alertdialog"
      aria-labelledby="unarchive-name-conflict-title"
      aria-describedby="unarchive-name-conflict-message"
    >
      <h3 id="unarchive-name-conflict-title" class="unarchive-name-conflict__title">
        Name already in use
      </h3>
      <p id="unarchive-name-conflict-message" class="unarchive-name-conflict__message">
        An active {{ lowerLabel }} named "{{ currentName }}" already exists. Enter a different name
        to unarchive this {{ lowerLabel }}.
      </p>
      <ExTextInput
        class="unarchive-name-conflict__input"
        :value="name"
        :label="`${label} name*`"
        :error="Boolean(displayedError)"
        :errorMessage="displayedError"
        :disabled="busy"
        @input="onNameInput"
      />
      <p
        v-if="errorMessage"
        class="unarchive-name-conflict__error"
        role="alert"
        data-testid="unarchive-name-conflict-error"
      >
        {{ errorMessage }}
      </p>
      <div class="unarchive-name-conflict__actions">
        <Button
          size="normal"
          outline="outline-hover"
          label="Cancel"
          buttonWidth="full"
          :fullwidth="busy ? 'disable' : 'hover'"
          data-testid="unarchive-name-conflict-cancel"
          @onClick="onCancel"
        />
        <Button
          size="normal"
          primary
          label="Rename and unarchive"
          buttonWidth="full"
          :fullwidth="canSubmit ? 'hover' : 'disable'"
          :loading="busy ? 'left' : null"
          data-testid="unarchive-name-conflict-submit"
          @onClick="onSubmit"
        />
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, watch } from "vue";
import Button from "@components/buttons/Button.vue";
import ExTextInput from "@components/TextInput/ExTextInput.vue";
import { useOrgObjectNameValidation } from "@/composables/useOrgObjectNameValidation";
import { UNARCHIVE_OBJECT_LABELS } from "@/composables/useUnarchiveOrgObject";
import { NAME_IN_USE } from "@components/constants/orgObjectMessages";

const props = defineProps({
  show: { type: Boolean, default: false },
  objectType: { type: String, default: "role" },
  currentName: { type: String, default: "" },
  objectId: { type: [String, Number], default: "" },
  accountId: { type: [String, Number], default: "" },
  jobFamilyId: { type: [String, Number], default: "" },
  busy: { type: Boolean, default: false },
  errorMessage: { type: String, default: "" },
  nameError: { type: String, default: "" },
});

const emit = defineEmits(["confirm", "close", "clear-error"]);

const { name, trimmedName, error, isValid } = useOrgObjectNameValidation({
  type: props.objectType,
  accountId: () => props.accountId,
  excludeId: () => props.objectId,
  jobFamilyId: () => props.jobFamilyId,
});

const label = computed(() => UNARCHIVE_OBJECT_LABELS[props.objectType] || "Object");
const lowerLabel = computed(() => label.value.toLowerCase());
const displayedError = computed(() => props.nameError || error.value);
const canSubmit = computed(
  () => !props.busy && trimmedName.value !== "" && isValid.value && !props.nameError
);

watch(
  () => props.show,
  (open) => {
    if (!open) return;
    name.value = props.currentName;
    error.value = NAME_IN_USE;
  },
  { immediate: true }
);

function onNameInput(value) {
  name.value = value || "";
  if (props.nameError || props.errorMessage) emit("clear-error");
}

function onSubmit() {
  if (!canSubmit.value) return;
  emit("confirm", trimmedName.value);
}

function onCancel() {
  if (props.busy) return;
  emit("close");
}

function onDialogInput(open) {
  if (!open) onCancel();
}
</script>

<style scoped>
.unarchive-name-conflict__card {
  border-radius: 12px;
}
.unarchive-name-conflict__title {
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}
.unarchive-name-conflict__message {
  margin: 4px 0 20px;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}
.unarchive-name-conflict__error {
  margin: 8px 0 0;
  font-size: 14px;
  line-height: 20px;
  color: #b42318;
}
.unarchive-name-conflict__actions {
  display: flex;
  gap: 12px;
  margin-top: 24px;
}
.unarchive-name-conflict__actions :deep(.storybook-button) {
  flex: 1 1 0;
  min-width: 0;
}
</style>
